import { useState, type ReactNode } from 'react';

export interface RecordTab {
  id: string;
  label: string;
  /** Shown under the label once known (related record counts). */
  count?: number | string | undefined;
  /**
   * Label of the top-level tab this tab sits under; the selected group's tabs show as a second
   * row. Ungrouped tabs, and groups of one, are top-level tabs themselves.
   */
  group?: string | undefined;
}

interface RecordTabsProps {
  /** Accessible name of the tab list, e.g. "Subscription record". */
  label: string;
  tabs: readonly RecordTab[];
  value: string;
  onChange: (id: string) => void;
  /** Content of the selected tab. */
  children: ReactNode;
}

interface TabGroup {
  key: string;
  label: string;
  tabs: RecordTab[];
}

/** Tabs by group, in order of first appearance; an ungrouped tab is a group of its own. */
function groupTabs(tabs: readonly RecordTab[]): TabGroup[] {
  const groups: TabGroup[] = [];
  const byLabel = new Map<string, TabGroup>();
  for (const tab of tabs) {
    if (tab.group === undefined) {
      groups.push({ key: tab.id, label: tab.label, tabs: [tab] });
      continue;
    }
    let group = byLabel.get(tab.group);
    if (!group) {
      group = {
        key: `group-${tab.group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        label: tab.group,
        tabs: [],
      };
      byLabel.set(tab.group, group);
      groups.push(group);
    }
    group.tabs.push(tab);
  }
  return groups;
}

/**
 * Record form: `section.card.card--flush.record` with the prototype's `.tabs[role=tablist]` and
 * one `role=tabpanel` for the selected tab (Main first, then related records). Grouped tabs add a
 * second tab row for the selected group; its tabs control the same panel.
 */
export function RecordTabs({ label, tabs, value, onChange, children }: RecordTabsProps) {
  const groups = groupTabs(tabs);
  const selected = groups.find((group) => group.tabs.some((tab) => tab.id === value));
  // The tab last open in each group, so switching back to a group returns to it.
  const [lastInGroup, setLastInGroup] = useState<Record<string, string>>({});
  const openGroup = (group: TabGroup) => {
    if (group === selected) return;
    if (selected) setLastInGroup((previous) => ({ ...previous, [selected.key]: value }));
    const last = lastInGroup[group.key];
    const first = group.tabs[0];
    const target = group.tabs.find((tab) => tab.id === last) ?? first;
    if (target) onChange(target.id);
  };

  return (
    <section className="card card--flush record" aria-label={label}>
      <div className="tabs" role="tablist" aria-label={label}>
        {groups.map((group) => {
          const single = group.tabs.length === 1 ? group.tabs[0] : undefined;
          return (
            <button
              key={group.key}
              id={single ? `record-tab-${single.id}` : `record-${group.key}`}
              className="tab"
              type="button"
              role="tab"
              aria-selected={group === selected}
              aria-controls="record-panel"
              onClick={() => {
                openGroup(group);
              }}
            >
              <span>{single ? single.label : group.label}</span>
              {single?.count !== undefined && <small>{single.count}</small>}
            </button>
          );
        })}
      </div>
      {selected && selected.tabs.length > 1 && (
        <div className="record__subtabs" role="tablist" aria-label={selected.label}>
          {selected.tabs.map((tab) => (
            <button
              key={tab.id}
              id={`record-tab-${tab.id}`}
              className="record__subtab"
              type="button"
              role="tab"
              aria-selected={tab.id === value}
              aria-controls="record-panel"
              onClick={() => {
                onChange(tab.id);
              }}
            >
              <span>{tab.label}</span>
              {tab.count !== undefined && <small>{tab.count}</small>}
            </button>
          ))}
        </div>
      )}
      <div
        className="record__panel"
        id="record-panel"
        role="tabpanel"
        aria-labelledby={`record-tab-${value}`}
      >
        {children}
      </div>
    </section>
  );
}
